import {ErrorMessage, Field, Form, Formik} from 'formik';
import {useTranslations} from 'next-intl';
import {FC} from 'react';
import { Button, Input } from '../design-system';
import TextArea from '@/components/design-system/Textarea';

const encode = (data) => {
  return Object.keys(data)
    .map(
      (key) =>
        encodeURIComponent(key.replace('_', '.')) +
        '=' +
        encodeURIComponent(data[key]),
    )
    .join('&');
};

interface Props {
  formType?: string;
  section?: string;
  onSent: () => void;
  showContact?: boolean;
  buttonId?: string;
}

const BusinessForm: FC<Props> = ({
  formType,
  section,
  onSent,
  buttonId,
  showContact,
}) => {
  const t = useTranslations('google-forms');
  const tBusiness = useTranslations('business');

  const getRequestsTypeOptions = () => {
    switch (section) {
      case 'virtual':
        return [
          {
            id: 'team-building-virtuale',
            name: tBusiness('VirtualEvents.Categories.card-1.title-heading'),
          },
          {
            id: 'aperitivo-aziendale-virtuale',
            name: tBusiness('VirtualEvents.Categories.card-2.title-heading'),
          },
          {
            id: 'consegna-a-domicilio-per-evento-virtuale',
            name: tBusiness('VirtualEvents.Categories.card-3.title-heading'),
          },
        ];
      case 'in-person':
        return [
          {
            id: 'eventi-aziendali',
            name: tBusiness('PhysicalEvents.Categories.card-1.title-heading'),
          },
          {
            id: 'team-building-aziendale',
            name: tBusiness('PhysicalEvents.Categories.card-2.title-heading'),
          },
          {
            id: 'catering-aziendale',
            name: tBusiness('PhysicalEvents.Categories.card-3.title-heading'),
          },
        ];
      case 'gift':
        return [
          {
            id: 'regalistica-aziendale',
            name: tBusiness('CorporateGifting.Categories.card-1.title-heading'),
          },
          {
            id: 'voucher-regalo',
            name: tBusiness('CorporateGifting.Categories.card-2.title-heading'),
          },
          {
            id: 'regalistica-natalizia',
            name: tBusiness('CorporateGifting.Categories.card-3.title-heading'),
          },
        ];
      case 'private':
        return [
          {
            id: 'private',
            name: 'Private',
          },
        ];
      default:
        return [
          {
            id: 'eventi-aziendali-catering',
            name: tBusiness('PhysicalEvents.Categories.card-1.title-heading'),
          },
          {
            id: 'team-building-catering',
            name: tBusiness('PhysicalEvents.Categories.card-2.title-heading'),
          },
          {
            id: 'catering-aziendale',
            name: tBusiness('PhysicalEvents.Categories.card-3.title-heading'),
          },
        ];
    }
  };

  return (
    <div>
      {showContact && (
        <>
          <h2 className="font-bold">
            {tBusiness('Contacts.title') + ' '}
            <a href="tel:+393405691136"
              className="underline hover:no-underline">
              +39 340 569 1136
            </a>
          </h2>
          <p className="text-sm">{tBusiness('Contacts.or')}</p>
        </>
      )}

      <h2 className="font-bold">{t('CateringForm.title')}</h2>
      <p>{t('CateringForm.subtitle')}</p>
      <Formik
        initialValues={{
          // Nome
          entry_619130984: '',
          // Cognome
          entry_1379503363: '',
          // Azienda
          entry_1468176690: '',
          // Mansione
          entry_1572492230: '',
          // E-mail
          entry_898298037: '',
          // Telefono
          entry_510288513: '',
          // la tua richiesta
          entry_803273733: '',
          // Tipo di richiesta
          entry_452551918: formType || '',
        }}
        initialStatus={{error: '', success: ''}}
        onSubmit={async (values, actions) => {
          try {
            const response = await fetch(
              'https://docs.google.com/forms/u/1/d/e/1FAIpQLSfeUdbHqQTtiD9MFoAYxW5rwxQTCnc8S0lT8dzswSMBtaCxCA/formResponse',
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                method: 'POST',
                mode: 'no-cors',
                body: encode(values),
              },
            );
            actions.setStatus({success: t('common.success')});
            onSent();
          } catch (err) {
            actions.setStatus({
              error: t('common.error'),
            });
          } finally {
            actions.setSubmitting(false);
          }
        }}
        render={({status, isSubmitting, values, handleChange}) => {
          return (
            <Form>
              {section !== 'private' && (
                <div className="mb-4">
                  <label className="text-sm block text-gray-500 mb-1">
                    {t('CateringForm.label.request-type')}
                  </label>
                  <Field
                    component="select"
                    disabled={isSubmitting}
                    name="entry_452551918"
                    className="form-input block w-full border-gray-300 rounded-md shadow-xs focus:border-gray-300 focus:ring-3 focus:ring-gray-200/50 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500 disabled:border-gray-200 disabled:shadow-none"
                    required
                  >
                    {getRequestsTypeOptions().map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Field>
                </div>
              )}
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:flex-1 mb-4">
                  <Field
                    as={Input}
                    label={t('CateringForm.label.name')}
                    placeholder={t('CateringForm.placeholder.name')}
                    maxLength={500}
                    disabled={isSubmitting}
                    value={values.entry_619130984}
                    onChange={handleChange}
                    onBlur={() => { return; }}
                    name="entry_619130984"
                    id="entry_619130984"
                    required
                  />
                  <ErrorMessage
                    name="entry_619130984"
                    className="text-error-dark"
                  />
                </div>
                <div className="mb-4 w-full sm:flex-1">
                  <Field
                    as={Input}
                    maxLength={500}
                    label={t('CateringForm.label.lastname')}
                    placeholder={t('CateringForm.placeholder.lastname')}
                    disabled={isSubmitting}
                    value={values.entry_1379503363}
                    onChange={handleChange}
                    onBlur={() => { return; }}
                    name="entry_1379503363"
                    id="entry_1379503363"
                    required
                  />
                  <ErrorMessage
                    name="entry_1379503363"
                    className="text-error-dark"
                  />
                </div>
              </div>
              {section !== 'private' && (
                <div className="mb-4">
                  <Field
                    as={Input}
                    maxLength={500}
                    label={t('CateringForm.label.company')}
                    placeholder={t('CateringForm.placeholder.company')}
                    disabled={isSubmitting}
                    value={values.entry_1468176690}
                    onChange={handleChange}
                    onBlur={() => { return; }}
                    name="entry_1468176690"
                    id="entry_1468176690"
                    required
                  />
                  <ErrorMessage
                    name="entry_1468176690"
                    className="text-error-dark"
                  />
                </div>
              )}
              {section === 'private' ? (
                <div className="flex flex-wrap gap-4">
                  <div className="mb-4 w-full sm:flex-1">
                    <Field
                      as={Input}
                      maxLength={500}
                      label={t('CateringForm.label.phone')}
                      placeholder={t('CateringForm.placeholder.phone')}
                      disabled={isSubmitting}
                      value={values.entry_510288513}
                      onChange={handleChange}
                      onBlur={() => { return; }}
                      name="entry_510288513"
                      id="entry_510288513"
                      required
                    />
                    <ErrorMessage
                      name="entry_510288513"
                      className="text-error-dark"
                    />
                  </div>
                  <div className="mb-4 w-full sm:flex-1">
                    <Field
                      as={Input}
                      maxLength={500}
                      label={t('CateringForm.label.email')}
                      placeholder={t('CateringForm.placeholder.email')}
                      disabled={isSubmitting}
                      value={values.entry_898298037}
                      onChange={handleChange}
                      onBlur={() => { return; }}
                      name="entry_898298037"
                      id="entry_898298037"
                      required
                    />
                    <ErrorMessage
                      name="entry_898298037"
                      className="text-error-dark"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  <div className="mb-4 w-full sm:flex-1">
                    <Field
                      as={Input}
                      maxLength={500}
                      label={t('CateringForm.label.job_position')}
                      placeholder={t('CateringForm.placeholder.job_position')}
                      disabled={isSubmitting}
                      value={values.entry_1572492230}
                      onChange={handleChange}
                      onBlur={() => { return; }}
                      name="entry_1572492230"
                      id="entry_1572492230"
                      required
                    />
                    <ErrorMessage
                      name="entry_1572492230"
                      className="text-error-dark"
                    />
                  </div>
                  <div className="mb-4 w-full sm:flex-1">
                    <Field
                      as={Input}
                      maxLength={500}
                      label={t('CateringForm.label.phone')}
                      placeholder={t('CateringForm.placeholder.phone')}
                      disabled={isSubmitting}
                      value={values.entry_510288513}
                      onChange={handleChange}
                      onBlur={() => { return; }}
                      name="entry_510288513"
                      id="entry_510288513"
                      required
                    />
                    <ErrorMessage
                      name="entry_510288513"
                      className="text-error-dark"
                    />
                  </div>
                  <div className="mb-4 w-full sm:flex-1">
                    <Field
                      as={Input}
                      maxLength={500}
                      label={t('CateringForm.label.email')}
                      placeholder={t('CateringForm.placeholder.email')}
                      disabled={isSubmitting}
                      value={values.entry_898298037}
                      onChange={handleChange}
                      onBlur={() => { return; }}
                      name="entry_898298037"
                      id="entry_898298037"
                      required
                    />
                    <ErrorMessage
                      name="entry_898298037"
                      className="text-error-dark"
                    />
                  </div>
                </div>
              )}
              <div className="mb-4">
                <Field
                  as={TextArea}
                  maxLength={500}
                  rows={4}
                  label={t('CateringForm.label.your-request')}
                  placeholder={t('CateringForm.placeholder.your-request')}
                  disabled={isSubmitting}
                  value={values.entry_803273733}
                  onChange={handleChange}
                  onBlur={() => { return; }}
                  name="entry_803273733"
                  required
                />
                <ErrorMessage name="entry_803273733" className="text-error-dark" />
              </div>
              <div>
                {status && status.error && (
                  <div className="md:w-1/2 mx-auto relative p-6 rounded-sm border bg-[#ff52521f] border-error-dark text-error-dark">
                    {status.error}
                  </div>
                )}
                {status && status.success && (
                  <div className="md:w-5/12 mx-auto relative p-6 rounded-sm border bg-[#43d9a31f] border-success-dark text-success-dark text-center">
                    {status.success}
                  </div>
                )}
              </div>
              {status && !status.success && (
                <Button
                  type="submit"
                  id={buttonId}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {t('common.send')}
                </Button>
              )}
            </Form>
          );
        }}
      />
    </div>
  );
};

export default BusinessForm;
